import "./TextArea.scss";

import cx from "classnames";
import KaboodleToaster from "components/common/KaboodleToaster";
import React, { useCallback, useState, useRef, useEffect } from "react";
import type { FieldErrors } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";

import AdvancedTextArea from "./AdvancedTextArea";

type BaseTextAreaProps = {
  label: string;
  name: string;
  value: string;
  readOnly?: boolean;
  errors?: FieldErrors;
  classNames?: string;
  placeholder?: string;
  advancedEdit?: boolean;
  allowMinimize?: boolean;
  onSave?: (value: string) => Promise<void>;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement> | string) => void;
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "name" | "value">;

export type TextAreaProps = BaseTextAreaProps;

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      readOnly,
      label,
      name,
      errors,
      value,
      onSave,
      classNames,
      placeholder,
      advancedEdit = false,
      allowMinimize = false,
      onChange,
      maxLength,
      ...rest
    },
    ref,
  ) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const prevValueRef = useRef(value);
    const latestValueRef = useRef(value);

    // Only adopt external value updates when the user is not editing.
    // Overwriting latestValueRef while focused poisons blur-save: TipTap may
    // still show the typed text, but latestValueRef === prevValueRef makes
    // the save a no-op (edits look stuck until refresh).
    useEffect(() => {
      if (isEditing) return;
      latestValueRef.current = value;
      prevValueRef.current = value;
    }, [value, isEditing]);

    const debouncedSave = useDebouncedCallback(
      async (val: string) => {
        if (!onSave || isSaving) return;
        try {
          setIsSaving(true);
          await onSave(val);
          prevValueRef.current = val;
          setHasPendingChanges(false);
        } catch (error) {
          console.error("TextArea save failed:", {
            name,
            error,
            value: val,
          });
        } finally {
          setIsSaving(false);
        }
      },
      800,
      { maxWait: 2000 },
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement> | string) => {
        const newValue = typeof e === "string" ? e : e.target.value;

        // if maxLength is set, enforce it
        if (maxLength && newValue.length > maxLength) {
          return; // prevent the change
        }

        // mark as editing when changes happen
        setIsEditing(true);
        setHasPendingChanges(true);
        latestValueRef.current = newValue;

        // Only autosave for the simple textarea. Advanced editor saves on blur.
        if (!advancedEdit) {
          debouncedSave(newValue);
        }

        // if the onChange prop is passed, call it with the event or string value
        if (onChange) {
          if (typeof e !== "string") {
            onChange(e);
          } else {
            // Create a synthetic event for the onChange handler
            const syntheticEvent = {
              target: { value: e },
            } as React.ChangeEvent<HTMLTextAreaElement>;
            onChange(syntheticEvent);
          }
        }
      },
      [debouncedSave, onChange, maxLength, advancedEdit],
    );

    // handle paste event and truncate if necessary
    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!maxLength) {
          return;
        }

        const pastedText = e.clipboardData.getData("text");
        const currentValue = e.currentTarget.value;
        const selectionStart = e.currentTarget.selectionStart || 0;
        const selectionEnd = e.currentTarget.selectionEnd || 0;

        // calculate what the new value would be after paste
        const newValue =
          currentValue.substring(0, selectionStart) +
          pastedText +
          currentValue.substring(selectionEnd);

        // if the new value would exceed maxLength, truncate and show toast
        if (newValue.length > maxLength) {
          e.preventDefault();

          // calculate how much we can paste
          const availableSpace =
            maxLength - (currentValue.length - (selectionEnd - selectionStart));
          const truncatedPastedText = pastedText.substring(0, availableSpace);

          // create the truncated value
          const truncatedValue =
            currentValue.substring(0, selectionStart) +
            truncatedPastedText +
            currentValue.substring(selectionEnd);

          // update the textarea value
          e.currentTarget.value = truncatedValue;

          handleChange({
            target: { value: truncatedValue },
          } as React.ChangeEvent<HTMLTextAreaElement>);

          KaboodleToaster.info(
            `Max text length is ${maxLength}. Your pasted content has been truncated.`,
          );
        }
      },
      [maxLength, handleChange],
    );

    const handleFocus = useCallback(() => {
      setIsEditing(true);
    }, []);

    const handleBlur = useCallback(() => {
      // Give time for any pending UI work; then, for advanced mode, save on blur.
      setTimeout(async () => {
        setIsEditing(false);

        if (advancedEdit && onSave && hasPendingChanges && !isSaving) {
          const val = latestValueRef.current;
          if (val !== prevValueRef.current) {
            try {
              setIsSaving(true);
              await onSave(val);
              prevValueRef.current = val;
              setHasPendingChanges(false);
            } catch (error) {
              console.error("TextArea save on blur failed:", {
                name,
                error,
                value: val,
              });
            } finally {
              setIsSaving(false);
            }
          }
        }
      }, 100);
    }, [advancedEdit, onSave, hasPendingChanges, isSaving, name]);

    const editor = advancedEdit ? (
      <AdvancedTextArea
        value={value}
        name={name}
        errors={errors}
        onChange={handleChange}
        isEditable={!readOnly}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    ) : (
      <textarea
        autoComplete="off"
        disabled={readOnly}
        ref={ref}
        value={value}
        name={name}
        onChange={handleChange}
        onPaste={handlePaste}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        maxLength={maxLength}
        {...rest}
      />
    );

    return (
      <div
        className={cx("textarea", classNames, {
          "textarea--advanced-mode": advancedEdit,
          "textarea--error": errors && errors[name],
          "textarea--disabled": readOnly,
          "textarea--minimizable": allowMinimize,
          "textarea--minimized": allowMinimize && isMinimized,
          // Card.scss grid-ifies every direct child of a "form" card into a
          // fixed 2-column (label | input) layout. With no label rendered,
          // the editor is the only grid item left and gets auto-placed into
          // that narrow first (label-sized) column instead of spanning both
          // -- this class tells Card.scss's grid to collapse to one column
          // so the editor gets the full width instead.
          "textarea--no-label": !label && !allowMinimize,
        })}
        role={errors && errors[name] && "alert"}
        title={errors && (errors[name]?.message as string)}
      >
        {allowMinimize ? (
          <div
            className="textarea__header"
            onClick={() => setIsMinimized(!isMinimized)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsMinimized(!isMinimized);
              }
            }}
          >
            <label htmlFor={name}>{label}</label>
            <i className="material-icons textarea__caret">
              {isMinimized ? "expand_more" : "expand_less"}
            </i>
          </div>
        ) : (
          label && <label htmlFor={name}>{label}</label>
        )}
        {(!allowMinimize || !isMinimized) && editor}
      </div>
    );
  },
);

TextArea.displayName = "TextArea";

export default TextArea;
